/**
 * PDP color-filtered product gallery (clip-noir-1, bleu-2, … + generic images).
 */
(() => {
  if (window.VidtoutProductGallery) return;

  const desktopGalleryMq = window.matchMedia('(min-width: 769px)');
  const galleryStates = new WeakMap();
  const initialized = new WeakSet();

  function getVisibleThumbs(productEl) {
    return Array.from(productEl.querySelectorAll('.product__thumb')).filter(
      (thumb) => !thumb.classList.contains('is-filtered-hidden')
    );
  }

  function thumbMatchesColor(thumb, colorKey) {
    if (thumb.dataset.colorIndexed !== 'true') return true;
    if (!colorKey) return false;
    return thumb.dataset.colorPrefix === colorKey;
  }

  function sortVisibleThumbs(thumbs) {
    if (window.VidtoutColorMedia) {
      return VidtoutColorMedia.sortGalleryThumbs(thumbs);
    }
    return thumbs.slice().sort((a, b) => {
      const aIndexed = a.dataset.colorIndexed === 'true';
      const bIndexed = b.dataset.colorIndexed === 'true';
      if (aIndexed && !bIndexed) return -1;
      if (!aIndexed && bIndexed) return 1;
      if (aIndexed && bIndexed) {
        const aSort = Number(a.dataset.colorSortIndex) || 0;
        const bSort = Number(b.dataset.colorSortIndex) || 0;
        if (aSort !== bSort) return aSort - bSort;
      }
      return Number(a.dataset.thumbIndex) - Number(b.dataset.thumbIndex);
    });
  }

  function initProductGallery(productEl) {
    if (initialized.has(productEl)) return;

    const gallery = productEl.querySelector('[data-vidtout-color-gallery]');
    if (!gallery) return;

    const mainImg = productEl.querySelector('#product-main-img');
    const track = productEl.querySelector('.product__thumbnails-track');
    const allThumbs = productEl.querySelectorAll('.product__thumb');
    const arrows = productEl.querySelectorAll('.product__thumb-arrow');
    const counterCurrent = productEl.querySelector('.product__image-counter-current');
    const counterTotal = productEl.querySelector('.product__image-counter-total');
    const statusEl = productEl.querySelector('#product-gallery-status');

    if (!mainImg || !track || !allThumbs.length) return;

    initialized.add(productEl);

    let thumbStart = 0;
    const visibleThumbs = 3;
    let loadToken = 0;

    function getMaxThumbStart(visibleCount) {
      return Math.max(0, visibleCount - visibleThumbs);
    }

    function updateArrowState(visibleCount) {
      const maxThumbStart = getMaxThumbStart(visibleCount);
      arrows.forEach((arrow) => {
        const dir = Number(arrow.dataset.thumbDir);
        if (dir < 0) {
          arrow.disabled = thumbStart <= 0;
        } else {
          arrow.disabled = thumbStart >= maxThumbStart;
        }
      });
    }

    function updateThumbPosition(visibleThumbsList) {
      const maxThumbStart = getMaxThumbStart(visibleThumbsList.length);
      thumbStart = Math.max(0, Math.min(thumbStart, maxThumbStart));
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      const thumbStep = visibleThumbsList[0] ? visibleThumbsList[0].offsetWidth + gap : 0;
      track.style.transform = `translate3d(${-thumbStart * thumbStep}px, 0, 0)`;
      updateArrowState(visibleThumbsList.length);
    }

    function updateImageCounter(visibleIndex, visibleCount) {
      if (counterCurrent) counterCurrent.textContent = String(visibleIndex + 1);
      if (counterTotal) counterTotal.textContent = String(visibleCount);
      if (statusEl) statusEl.textContent = `Image ${visibleIndex + 1} of ${visibleCount}`;
    }

    function keepActiveThumbInView(thumb, visibleThumbsList) {
      const index = visibleThumbsList.indexOf(thumb);
      if (index < 0) return;
      if (index < thumbStart) thumbStart = index;
      if (index >= thumbStart + visibleThumbs) thumbStart = index - visibleThumbs + 1;
      updateThumbPosition(visibleThumbsList);
    }

    function setActiveImage(thumb) {
      const token = ++loadToken;
      mainImg.classList.add('is-swapping');
      mainImg.src = thumb.dataset.full;

      if (thumb.dataset.srcset) {
        mainImg.srcset = thumb.dataset.srcset.trim();
        mainImg.sizes = thumb.dataset.sizes;
      } else {
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
      }

      const altImg = thumb.querySelector('.product__thumb-img');
      if (altImg?.alt) mainImg.alt = altImg.alt;

      function afterReady() {
        if (token !== loadToken) return;
        mainImg.classList.remove('is-swapping');
      }

      if (mainImg.complete) {
        afterReady();
      } else {
        mainImg.addEventListener('load', afterReady, { once: true });
      }

      mainImg.decode?.().then(afterReady).catch(afterReady);
    }

    function selectThumb(thumb, visibleThumbsList, force) {
      if (!thumb || thumb.classList.contains('is-filtered-hidden')) return;
      const visibleIndex = visibleThumbsList.indexOf(thumb);
      if (visibleIndex < 0) return;
      if (!force && thumb.classList.contains('is-active')) return;

      setActiveImage(thumb);
      allThumbs.forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      keepActiveThumbInView(thumb, visibleThumbsList);
      updateImageCounter(visibleIndex, visibleThumbsList.length);
    }

    function applyColorFilter(colorKey) {
      allThumbs.forEach((thumb) => {
        const visible = thumbMatchesColor(thumb, colorKey);
        thumb.classList.toggle('is-filtered-hidden', !visible);
        thumb.hidden = !visible;
        if (!visible) thumb.classList.remove('is-active');
      });

      let visibleList = sortVisibleThumbs(getVisibleThumbs(productEl));
      thumbStart = 0;

      if (visibleList.length) {
        selectThumb(visibleList[0], visibleList, true);
      }

      const thumbsNav = productEl.querySelector('.product__thumbs-nav');
      if (thumbsNav) thumbsNav.hidden = visibleList.length <= 1;

      updateThumbPosition(visibleList);
    }

    allThumbs.forEach((thumb) => {
      thumb.addEventListener('mouseenter', () => {
        if (!desktopGalleryMq.matches) return;
        selectThumb(thumb, getVisibleThumbs(productEl));
      });
      thumb.addEventListener('click', () => {
        selectThumb(thumb, getVisibleThumbs(productEl));
      });
    });

    arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        thumbStart += Number(arrow.dataset.thumbDir);
        updateThumbPosition(getVisibleThumbs(productEl));
      });
    });

    new ResizeObserver(() => {
      requestAnimationFrame(() => updateThumbPosition(getVisibleThumbs(productEl)));
    }).observe(track);

    galleryStates.set(productEl, { applyColorFilter });

    const hasIndexedThumbs = Array.from(allThumbs).some(
      (thumb) => thumb.dataset.colorIndexed === 'true'
    );
    if (hasIndexedThumbs || gallery.dataset.initialColorKey) {
      applyColorFilter(gallery.dataset.initialColorKey || '');
    } else {
      requestAnimationFrame(() => {
        const visibleList = getVisibleThumbs(productEl);
        updateThumbPosition(visibleList);
        const active = productEl.querySelector('.product__thumb.is-active');
        if (active) updateImageCounter(visibleList.indexOf(active), visibleList.length);
      });
    }
  }

  function bootGalleries() {
    document.querySelectorAll('.product[data-section-id]').forEach(initProductGallery);
  }

  function resolveColorKeyForSection(section, productEl) {
    const gallery = productEl.querySelector('[data-vidtout-color-gallery]');
    const dataEl = gallery?.querySelector('.vidtout-color-image-data');
    const catalog = window.VidtoutColorMedia?.parseCatalog(dataEl?.textContent || '');

    if (catalog && window.VidtoutColorMedia) {
      const fromPicker = VidtoutColorMedia.colorKeyFromPicker(section, catalog);
      if (fromPicker) return fromPicker;
    }

    if (gallery?.dataset.initialColorKey) return gallery.dataset.initialColorKey;
    return '';
  }

  function colorKeyFromEvent(event) {
    const el = event.target;
    if (el instanceof HTMLElement && el.dataset.colorKey) {
      return el.dataset.colorKey;
    }
    return '';
  }

  function handleVariantChange(event) {
    const section = event.target.closest('.shopify-section');
    if (!section) return;

    const productEl = section.querySelector('.product[data-section-id]');
    if (!productEl) return;

    if (!galleryStates.has(productEl)) initProductGallery(productEl);

    const state = galleryStates.get(productEl);
    if (!state?.applyColorFilter) return;

    const gallery = productEl.querySelector('[data-vidtout-color-gallery]');
    const dataEl = gallery?.querySelector('.vidtout-color-image-data');
    const catalog = window.VidtoutColorMedia?.parseCatalog(dataEl?.textContent || '');

    let colorKey = colorKeyFromEvent(event);
    if (!colorKey && catalog && window.VidtoutColorMedia) {
      colorKey = VidtoutColorMedia.resolveColorKey(
        section,
        catalog,
        event.detail?.resource
      );
    }
    if (!colorKey) colorKey = resolveColorKeyForSection(section, productEl);

    if (gallery && colorKey) gallery.dataset.initialColorKey = colorKey;
    state.applyColorFilter(colorKey);
  }

  document.addEventListener('variant:update', handleVariantChange);
  document.addEventListener('variant:selected', handleVariantChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootGalleries, { once: true });
  } else {
    bootGalleries();
  }

  window.VidtoutProductGallery = { bootGalleries, initProductGallery };
})();
